/**
 * WL-K15 — KontierungVorschlagPanel: the StB-only Auto-Kontierung panel.
 *
 * Shows the last-confirmed booking suggestion for the supplier of one inbound
 * document, lets the Steuerberater OVERRIDE it, and „übernehmen" writes the choice
 * into the (Kanzlei × Lieferant) memory (deterministic last-write-wins, server-side).
 *
 * ⚠️ §33-StBerG-Grenzfall — the framing decides admissibility. The §6e disclaimer
 * (`framing-k15-kontierung`) is ALWAYS rendered, verbatim: „unverbindlicher
 * Vorschlag … keine steuerliche Beratung … trifft Ihr Steuerberater". NO correctness
 * claim, no client-facing promise.
 *
 * SICHERHEIT / SCOPE: purely cosmetic. Every hard check (Loop-B-Beta + ACTIVE advisor
 * membership + the field whitelist) is enforced server-side in the callables. This
 * component is rendered EXCLUSIVELY inside the StB-only AdvisorCockpit — NEVER in the
 * Mandanten-UI (the owner-facing InboundDocumentDetail does not import it, and the
 * server rejects a non-advisor caller with `permission-denied`).
 *
 * DE ist maßgeblich: labels are inline German (StB-Kontext ist DE; die §6e-Rechtstext-
 * Passage MUSS exakt deutsch sein), keine i18n-Keys über 6 Locales (Beta-Panel).
 *
 * @module components/moat/KontierungVorschlagPanel
 */

import React, { useEffect, useState } from 'react';
import { IconSuccess, IconWarning } from '../../icons';
import {
  getKontierungVorschlag,
  confirmKontierung,
  KONTIERUNG_FRAMING,
  type KontierungVorschlag,
  type KontierungSkr,
} from '../../api/kontierungApi';

/** DATEV Sachkonto: 4–8 digits (mirrors the server regex). */
const ACCOUNT_RE = /^\d{4,8}$/;
/** DATEV BU-/Steuerschlüssel: 1–3 digits (mirrors the server regex). */
const BUKEY_RE = /^\d{1,3}$/;
const SKR_VALUES: readonly KontierungSkr[] = ['SKR03', 'SKR04'];

/** Editable, overridable form state (all strings for the inputs). */
interface FormState {
  skrAccount: string;
  buKey: string;
  skr: '' | KontierungSkr;
}

/** Pre-fills the (overridable) form from a loaded suggestion. */
function toForm(v: KontierungVorschlag): FormState {
  return {
    skrAccount: v.skrAccount ?? '',
    buKey: v.buKey ?? '',
    skr: v.skr ?? '',
  };
}

type Status =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'saveError' };

/**
 * The Auto-Kontierung panel for a single inbound document within the StB cockpit.
 *
 * @param mandantUid the currently selected mandant scope.
 * @param docId the inbound document the suggestion is for.
 */
const KontierungVorschlagPanel: React.FC<{
  mandantUid: string;
  docId: string;
}> = ({ mandantUid, docId }) => {
  const [vorschlag, setVorschlag] = useState<KontierungVorschlag | null>(null);
  const [form, setForm] = useState<FormState>({ skrAccount: '', buKey: '', skr: '' });
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: 'loading' });
    getKontierungVorschlag(mandantUid, docId)
      .then((v) => {
        if (cancelled) return;
        setVorschlag(v);
        setForm(toForm(v));
        setStatus({ kind: 'ready' });
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [mandantUid, docId]);

  const update = (patch: Partial<FormState>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
    setStatus((prev) =>
      prev.kind === 'saved' || prev.kind === 'saveError' ? { kind: 'ready' } : prev,
    );
  };

  const accountValid = ACCOUNT_RE.test(form.skrAccount.trim());
  const buKeyRaw = form.buKey.trim();
  const buKeyValid = buKeyRaw === '' || BUKEY_RE.test(buKeyRaw);
  // The StB must supply a complete booking (account + SKR); the Steuerschlüssel is
  // optional (Automatikkonto). „übernehmen" is only enabled for a valid, complete form.
  const canSubmit = accountValid && buKeyValid && form.skr !== '';

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!canSubmit || form.skr === '') return;
    setStatus({ kind: 'saving' });
    try {
      const res = await confirmKontierung(mandantUid, docId, {
        skrAccount: form.skrAccount.trim(),
        buKey: buKeyRaw === '' ? null : buKeyRaw,
        skr: form.skr,
      });
      setVorschlag(res.proposal);
      setStatus({ kind: 'saved' });
    } catch {
      setStatus({ kind: 'saveError' });
    }
  };

  if (status.kind === 'loading') {
    return (
      <div className="rounded-md bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-muted">
        Buchungsvorschlag wird geladen …
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div className="rounded-md bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-[var(--tone-danger-fg)]">
        Der Buchungsvorschlag konnte nicht geladen werden.
      </div>
    );
  }

  const hasProposal = vorschlag?.hasProposal === true;
  const framing = vorschlag?.framing || KONTIERUNG_FRAMING;

  return (
    <div className="rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex items-center rounded bg-[var(--tone-accent-bg)] px-2 py-0.5 text-xs font-semibold text-[var(--tone-accent-fg)]">
          Buchungsvorschlag
        </span>
        {hasProposal ? (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--tone-success-fg)]">
            <IconSuccess size={12} />
            zuletzt bestätigt
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            <IconWarning size={12} className="text-[var(--tone-warning-fg)]" />
            noch kein Vorschlag — bitte kontieren
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">Konto (SKR)</span>
            <input
              type="text"
              inputMode="numeric"
              value={form.skrAccount}
              onChange={(e) => update({ skrAccount: e.target.value })}
              placeholder="z. B. 4980"
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            />
            <span
              className={`mt-1 block text-xs ${
                form.skrAccount.trim() !== '' && !accountValid ? 'text-[var(--tone-danger-fg)]' : 'text-faint'
              }`}
            >
              4–8-stelliges Sachkonto
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">Steuerschlüssel</span>
            <input
              type="text"
              inputMode="numeric"
              value={form.buKey}
              onChange={(e) => update({ buKey: e.target.value })}
              placeholder="optional"
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            />
            <span
              className={`mt-1 block text-xs ${
                buKeyRaw !== '' && !buKeyValid ? 'text-[var(--tone-danger-fg)]' : 'text-faint'
              }`}
            >
              BU-Schlüssel (optional)
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-secondary">Kontenrahmen</span>
            <select
              value={form.skr}
              onChange={(e) => update({ skr: e.target.value as '' | KontierungSkr })}
              className="w-full rounded-md border border-strong bg-surface px-3 py-2 text-sm text-primary focus:border-indigo-400 focus:outline-none"
            >
              <option value="">—</option>
              {SKR_VALUES.map((skr) => (
                <option key={skr} value={skr}>
                  {skr}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit || status.kind === 'saving'}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status.kind === 'saving' ? 'Wird übernommen …' : 'Übernehmen'}
          </button>
          {status.kind === 'saved' && (
            <span className="text-sm text-[var(--tone-success-fg)]">Übernommen — als Vorschlag gemerkt.</span>
          )}
          {status.kind === 'saveError' && (
            <span className="text-sm text-[var(--tone-danger-fg)]">Konnte nicht gespeichert werden.</span>
          )}
        </div>
      </form>

      {/* §6e-Framing — IMMER sichtbar, 1:1. Unverbindlicher Vorschlag, StB entscheidet. */}
      <p className="mt-3 text-xs leading-relaxed text-muted">{framing}</p>
    </div>
  );
};

export default KontierungVorschlagPanel;
